/**
 * SOLID Review
 * S — Strong. One concern: declare the two sockets ComponentFrontendFramework requires — what React calls
 *     and what it renders from.
 * O — Good. A socket is one field.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two `RequiredPortWithDefault<T>` fields, one interface each.
 * D — Strong. The kernel, the Application's port types and the defaults each socket degrades to — all of them
 *     `application/ports/` (FE7: the Framework imports Application ports only).
 */
import { PortHolder } from '@ml3d/kernel';
import { RequiredPortWithDefault } from '@ml3d/kernel/required-with-default';
import { ClientUseCasesContract, type IPortClientUseCases } from '../../application/ports/ClientUseCases.contract';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';
import { unavailable } from '../../application/ports/unavailable';
import { NULL_CLIENT_VIEW } from '../../application/ports/nullClientView';

/**
 * Every socket ComponentFrontendFramework requires (frontend strict-ports plan, FE1d, 2026-10-03) — the
 * twin of the backend's `ComponentFrameworkRequiredPorts`. The Framework provides nothing, so it has no
 * provided holder (the owner's "no empty holders").
 *
 * THE TWO SOCKETS — what React does and what it shows (FE1):
 *   - `ClientUseCases`: every move and every catch-up, contracted, answering `UNAVAILABLE` unwired;
 *   - `ClientStateView`: the store by reference, UNCONTRACTED by design (`ports:check`'s
 *     VIEW_BY_REFERENCE): `useSyncExternalStore` needs the store's own object back.
 *
 * THE LEGACY SOCKETS ARE GONE (FE6a, 2026-10-04). Eleven adapter sockets sat here beside these two from
 * FE1 — the adapter objects React called directly — until each phase moved its flows behind
 * `ClientUseCases`: FE4a–FE5b deleted six, FE6a the last five. Exactly the two sockets the plan names.
 *
 * Basename starts with `Component` and every constructor carries its `<T>` (§28.105). Walked because this
 * class extends `PortHolder`.
 */
export class ComponentFrontendFrameworkRequiredPorts extends PortHolder {
  readonly clientUseCasesSocket = new RequiredPortWithDefault<IPortClientUseCases>(
    'ClientUseCases', unavailable<IPortClientUseCases>(ClientUseCasesContract), ClientUseCasesContract);
  readonly clientStateViewSocket = new RequiredPortWithDefault<IPortClientStateView>('ClientStateView', NULL_CLIENT_VIEW);
}
